// VRT custom-orders "Kit range" + "Project brief" — two web components that
// share one selection store so picking a garment in the range grid lands it
// in the brief wizard below, and vice versa.
//
// <vrt-kit-range>  — enhances the server-rendered garment grid in
//                     sections/vrt-custom-range.liquid: activity-chip
//                     filtering + "Add to your brief" toggle. No-JS: the
//                     grid is a fully static catalogue (chips + toggle
//                     buttons ship `hidden`, unhidden here once alive).
// <vrt-brief>       — the 3-step project-brief wizard in
//                     sections/vrt-custom-brief.liquid. Config-driven (a
//                     JSON island carries the garment key+label list so this
//                     works even if the range section is removed) and posts
//                     through a real hidden {% form 'contact' %} using the
//                     same hCaptcha + upload adapters proven in
//                     vrt-kit-builder.js (copy-adapted verbatim, not
//                     refactored — that file stays untouched).
//
// Sync contract: sessionStorage['vrt-brief-selection-v1'] (JSON array of
// garment keys) is the source of truth; a document-level
// CustomEvent('vrt:brief-selection', {detail:{keys, source}}) tells the
// *other* component to re-read the store (each component ignores events
// carrying its own source). Both components read the store fresh in
// connectedCallback, so the selection (and the wizard's own field draft,
// stored separately under 'vrt-brief-draft-v1') survive a reload. Stored
// keys that no longer match the live catalogue are dropped on read.
//
// Focus-safety: each component builds its interactive shell ONCE via
// `this.innerHTML =` in connectedCallback; every later state change is a
// targeted querySelector + class/attribute/textContent toggle, never a
// second full-innerHTML render, so the brief wizard's text inputs/textarea
// never lose focus/caret/value mid-flow.

(function () {
  'use strict';

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
  const SELECTION_KEY = 'vrt-brief-selection-v1';
  const DRAFT_KEY = 'vrt-brief-draft-v1';
  const SELECTION_EVENT = 'vrt:brief-selection';

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }

  function readSelection() {
    try {
      const raw = sessionStorage.getItem(SELECTION_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function writeSelection(keys, source) {
    try { sessionStorage.setItem(SELECTION_KEY, JSON.stringify(keys)); } catch (e) { /* best-effort */ }
    document.dispatchEvent(new CustomEvent(SELECTION_EVENT, { detail: { keys, source } }));
  }

  function filterKnownKeys(keys, knownSet) {
    return (keys || []).filter((k) => knownSet.has(k));
  }

  function readDraft() {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      const obj = raw ? JSON.parse(raw) : null;
      return obj && typeof obj === 'object' ? obj : null;
    } catch (e) {
      return null;
    }
  }

  function writeDraft(draft) {
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft)); } catch (e) { /* best-effort */ }
  }

  function clearDraft() {
    try { sessionStorage.removeItem(DRAFT_KEY); } catch (e) { /* noop */ }
  }

  function debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), wait);
    };
  }

  let uploadSeq = 0;

  // ------------------------------------------------------------------
  // <vrt-kit-range>
  // ------------------------------------------------------------------

  class VrtKitRange extends HTMLElement {
    connectedCallback() {
      this.gridEl = this.querySelector('[data-curange-grid]') || this;
      this.cards = Array.from(this.querySelectorAll('[data-curange-card]'));
      this.chipsEl = this.querySelector('[data-curange-chips]');
      this.activeCode = '';

      this.reorderCards();

      const known = new Set(this.cards.map((c) => c.dataset.key));
      this.knownKeys = known;
      this.selection = new Set(filterKnownKeys(readSelection(), known));
      this.applySelectionToCards();

      if (this.chipsEl) this.chipsEl.hidden = false;
      this.cards.forEach((card) => {
        const btn = card.querySelector('[data-curange-add]');
        if (btn) btn.hidden = false;
      });

      this.addEventListener('click', this.onClick.bind(this));
      this.boundOnSelectionEvent = this.onSelectionEvent.bind(this);
      document.addEventListener(SELECTION_EVENT, this.boundOnSelectionEvent);
    }

    disconnectedCallback() {
      document.removeEventListener(SELECTION_EVENT, this.boundOnSelectionEvent);
    }

    onSelectionEvent(e) {
      if (!e.detail || e.detail.source === 'range') return;
      this.selection = new Set(filterKnownKeys(e.detail.keys || [], this.knownKeys));
      this.applySelectionToCards();
    }

    // Cards render in whatever order the metaobject list API returns
    // (admin order — Liquid `sort` on metaobject values is unproven); this
    // re-appends them by their `data-order` (sort_order, default 9999) once
    // on connect. No-JS keeps the admin order as-is.
    reorderCards() {
      if (!this.gridEl || this.cards.length < 2) return;
      const sorted = [...this.cards].sort((a, b) => (
        (parseInt(a.dataset.order, 10) || 9999) - (parseInt(b.dataset.order, 10) || 9999)
      ));
      sorted.forEach((card) => this.gridEl.appendChild(card));
      this.cards = sorted;
    }

    onClick(e) {
      let t;
      if ((t = e.target.closest('[data-curange-chip]'))) {
        this.activeCode = t.dataset.code || '';
        this.applyFilter();
        return;
      }
      if ((t = e.target.closest('[data-curange-add]'))) {
        const card = t.closest('[data-curange-card]');
        if (card) this.toggleKey(card.dataset.key);
      }
    }

    applyFilter() {
      if (this.chipsEl) {
        this.chipsEl.querySelectorAll('[data-curange-chip]').forEach((chip) => {
          chip.classList.toggle('is-active', (chip.dataset.code || '') === this.activeCode);
        });
      }
      this.cards.forEach((card) => {
        const activities = card.dataset.activities || '';
        const show = !this.activeCode || activities.indexOf(this.activeCode) !== -1;
        card.hidden = !show;
      });
    }

    toggleKey(key) {
      if (!key) return;
      if (this.selection.has(key)) this.selection.delete(key);
      else this.selection.add(key);
      this.applySelectionToCards();
      writeSelection(Array.from(this.selection), 'range');
    }

    applySelectionToCards() {
      this.cards.forEach((card) => {
        const isSel = this.selection.has(card.dataset.key);
        card.classList.toggle('is-selected', isSel);
        const btn = card.querySelector('[data-curange-add]');
        if (!btn) return;
        btn.textContent = isSel ? '✓ In your brief' : 'Add to your brief';
        btn.classList.toggle('is-active', isSel);
        btn.setAttribute('aria-pressed', isSel ? 'true' : 'false');
      });
    }
  }

  if (!customElements.get('vrt-kit-range')) {
    customElements.define('vrt-kit-range', VrtKitRange);
  }

  // ------------------------------------------------------------------
  // <vrt-brief> — 3-step project brief wizard
  // ------------------------------------------------------------------

  const STEP_TITLES = ['Your crew', 'The kit', 'Design & delivery'];
  const DEFAULT_ACTIVITY = 'Running / trail';
  const DEFAULT_QUANTITY = '1 – 4 items';
  const DEFAULT_EVENT_DATE = 'No fixed date';
  const DEFAULT_DELIVERY = 'One batch to one address';

  class VrtBrief extends HTMLElement {
    connectedCallback() {
      this.config = this.readConfig();
      this.garmentsByKey = {};
      this.config.garments.forEach((g) => { this.garmentsByKey[g.key] = g; });
      this.knownGarmentKeys = new Set(this.config.garments.map((g) => g.key));

      this.state = {
        step: 1,
        submitted: false,
        club: '',
        contactName: '',
        email: '',
        activity: DEFAULT_ACTIVITY,
        quantity: DEFAULT_QUANTITY,
        eventDate: DEFAULT_EVENT_DATE,
        design: '',
        delivery: DEFAULT_DELIVERY,
        rightsConfirmed: false,
        files: [],
        ref: null,
      };
      this.selection = new Set(filterKnownKeys(readSelection(), this.knownGarmentKeys));
      this.applyDraft(readDraft());

      this.hiddenForm = this.config.submit && this.config.submit.formSelector
        ? document.querySelector(this.config.submit.formSelector)
        : null;

      this.innerHTML = this.template();
      this.cacheEls();
      this.bindEvents();

      this.clubEl.value = this.state.club;
      this.contactNameEl.value = this.state.contactName;
      this.emailEl.value = this.state.email;
      this.activityEl.value = this.state.activity;
      this.quantityEl.value = this.state.quantity;
      this.eventDateEl.value = this.state.eventDate;
      this.designEl.value = this.state.design;
      this.deliveryEl.value = this.state.delivery;
      this.rightsEl.checked = this.state.rightsConfirmed;

      this.renderChips();
      this.renderUploadSlot();
      this.goToStep(this.state.step, true);

      this.boundOnSelectionEvent = this.onSelectionEvent.bind(this);
      document.addEventListener(SELECTION_EVENT, this.boundOnSelectionEvent);
    }

    disconnectedCallback() {
      document.removeEventListener(SELECTION_EVENT, this.boundOnSelectionEvent);
    }

    onSelectionEvent(e) {
      if (!e.detail || e.detail.source === 'brief') return;
      this.selection = new Set(filterKnownKeys(e.detail.keys || [], this.knownGarmentKeys));
      this.renderChips();
    }

    readConfig() {
      const el = this.querySelector('[data-brief-config]');
      let parsed = {};
      if (el) {
        try { parsed = JSON.parse(el.textContent); } catch (e) { parsed = {}; }
      }
      return {
        submit: parsed.submit || { mode: 'contact', formSelector: '#CustomBriefForm' },
        garments: Array.isArray(parsed.garments) ? parsed.garments : [],
        upload: parsed.upload || { mode: 'none' },
      };
    }

    // ---------------------------------------------------------------
    // Draft persistence — wizard field answers only (garment selection
    // lives in the shared SELECTION_KEY store above, not here).
    // ---------------------------------------------------------------

    persistDraft() {
      writeDraft({
        step: this.state.step,
        club: this.state.club,
        contactName: this.state.contactName,
        email: this.state.email,
        activity: this.state.activity,
        quantity: this.state.quantity,
        eventDate: this.state.eventDate,
        design: this.state.design,
        delivery: this.state.delivery,
        rightsConfirmed: this.state.rightsConfirmed,
        files: this.state.files
          .filter((f) => f.status === 'done')
          .map((f) => ({ id: f.id, name: f.name, status: f.status, url: f.url })),
      });
    }

    applyDraft(draft) {
      if (!draft) return;
      if (typeof draft.club === 'string') this.state.club = draft.club;
      if (typeof draft.contactName === 'string') this.state.contactName = draft.contactName;
      if (typeof draft.email === 'string') this.state.email = draft.email;
      if (typeof draft.activity === 'string') this.state.activity = draft.activity;
      if (typeof draft.quantity === 'string') this.state.quantity = draft.quantity;
      if (typeof draft.eventDate === 'string') this.state.eventDate = draft.eventDate;
      if (typeof draft.design === 'string') this.state.design = draft.design;
      if (typeof draft.delivery === 'string') this.state.delivery = draft.delivery;
      if (typeof draft.rightsConfirmed === 'boolean') this.state.rightsConfirmed = draft.rightsConfirmed;
      if (Array.isArray(draft.files)) this.state.files = draft.files;
      if (draft.step === 2 || draft.step === 3) this.state.step = draft.step;
    }

    // ---------------------------------------------------------------
    // Shell (built once)
    // ---------------------------------------------------------------

    template() {
      return `
        <div class="vrt-brief__stepnote" data-brief-stepnote></div>
        <div data-brief-body>
          <div class="vrt-brief__step" data-brief-step="1">
            <div class="vrt-brief__grid">
              <label class="vrt-brief__field vrt-brief__field--full">
                <span class="vrt-brief__label">Club / event / group name</span>
                <input type="text" class="vrt-brief__input" data-brief-club placeholder="e.g. MSTG Run Club">
              </label>
              <label class="vrt-brief__field">
                <span class="vrt-brief__label">Contact name</span>
                <input type="text" class="vrt-brief__input" data-brief-contactname placeholder="Your name">
              </label>
              <label class="vrt-brief__field">
                <span class="vrt-brief__label">Email</span>
                <input type="email" class="vrt-brief__input" data-brief-email placeholder="you@club.com">
              </label>
              <label class="vrt-brief__field vrt-brief__field--full">
                <span class="vrt-brief__label">Activity</span>
                <select class="vrt-brief__input" data-brief-activity>
                  <option>Running / trail</option>
                  <option>Cycling / MTB</option>
                  <option>Event / race</option>
                  <option>Gym / training</option>
                  <option>Team sport</option>
                  <option>Mixed</option>
                </select>
              </label>
            </div>
            <span class="vrt-brief__fielderror" data-brief-emailerror hidden>Enter a valid email so we can reply.</span>
            <span class="vrt-brief__fielderror" data-brief-step1error hidden>Enter a club/group name and a valid email to continue.</span>
            <button type="button" class="vrt-brief__btn vrt-brief__btn--primary vrt-brief__btn--full" data-brief-next="2">Next: the kit →</button>
          </div>

          <div class="vrt-brief__step" data-brief-step="2" hidden>
            <span class="vrt-brief__label">Garments (picked from the range above)</span>
            <div class="vrt-brief__chips" data-brief-chips></div>
            <div class="vrt-brief__grid">
              <label class="vrt-brief__field">
                <span class="vrt-brief__label">Approx. quantity</span>
                <select class="vrt-brief__input" data-brief-quantity>
                  <option>1 – 4 items</option>
                  <option>5 – 25</option>
                  <option>26 – 50</option>
                  <option>51 – 100</option>
                  <option>100+</option>
                </select>
              </label>
              <label class="vrt-brief__field">
                <span class="vrt-brief__label">Fixed event date?</span>
                <select class="vrt-brief__input" data-brief-eventdate>
                  <option>No fixed date</option>
                  <option>Yes, within 4 weeks</option>
                  <option>Yes, 1 – 3 months away</option>
                  <option>Yes, 3+ months away</option>
                </select>
              </label>
            </div>
            <div class="vrt-brief__hint">A fixed date changes our advice, so we ask early. We will not accept a date we can't hit.</div>
            <div class="vrt-brief__btnrow">
              <button type="button" class="vrt-brief__btn vrt-brief__btn--outline" data-brief-back="1">← Back</button>
              <button type="button" class="vrt-brief__btn vrt-brief__btn--primary" data-brief-next="3">Next: design &amp; delivery →</button>
            </div>
          </div>

          <div class="vrt-brief__step" data-brief-step="3" hidden>
            <label class="vrt-brief__field">
              <span class="vrt-brief__label">Design direction</span>
              <textarea class="vrt-brief__input vrt-brief__input--textarea" data-brief-design rows="3" placeholder="Colours, references, sponsors, or 'start from our current kit'. A rough idea is enough."></textarea>
            </label>
            <div data-brief-uploadslot></div>
            <label class="vrt-brief__field">
              <span class="vrt-brief__label">Delivery preference</span>
              <select class="vrt-brief__input" data-brief-delivery>
                <option>One batch to one address</option>
                <option>Direct to each member</option>
                <option>Hybrid: batch launch, direct reorders</option>
                <option>Not sure yet</option>
              </select>
            </label>
            <label class="vrt-brief__checkrow">
              <input type="checkbox" data-brief-rights>
              <span>I have the right to use the logos and artwork I'm supplying.</span>
            </label>
            <span class="vrt-brief__fielderror" data-brief-rightserror hidden>Please confirm you have the right to use this artwork.</span>
            <div class="vrt-brief__btnrow">
              <button type="button" class="vrt-brief__btn vrt-brief__btn--outline" data-brief-back="2">← Back</button>
              <button type="button" class="vrt-brief__btn vrt-brief__btn--primary" data-brief-submit>Send the brief →</button>
            </div>
          </div>
        </div>

        <div class="vrt-brief__done" data-brief-done hidden></div>
      `;
    }

    cacheEls() {
      this.railCard = this.closest('[data-brief-card]');
      this.dotsEls = this.railCard ? Array.from(this.railCard.querySelectorAll('[data-brief-dot]')) : [];

      this.stepnoteEl = this.querySelector('[data-brief-stepnote]');
      this.stepEls = Array.from(this.querySelectorAll('[data-brief-step]'));
      this.wizardBodyEl = this.querySelector('[data-brief-body]');
      this.doneEl = this.querySelector('[data-brief-done]');

      this.clubEl = this.querySelector('[data-brief-club]');
      this.contactNameEl = this.querySelector('[data-brief-contactname]');
      this.emailEl = this.querySelector('[data-brief-email]');
      this.emailErrorEl = this.querySelector('[data-brief-emailerror]');
      this.step1ErrorEl = this.querySelector('[data-brief-step1error]');
      this.activityEl = this.querySelector('[data-brief-activity]');

      this.chipsEl = this.querySelector('[data-brief-chips]');
      this.quantityEl = this.querySelector('[data-brief-quantity]');
      this.eventDateEl = this.querySelector('[data-brief-eventdate]');

      this.designEl = this.querySelector('[data-brief-design]');
      this.uploadSlotEl = this.querySelector('[data-brief-uploadslot]');
      this.deliveryEl = this.querySelector('[data-brief-delivery]');
      this.rightsEl = this.querySelector('[data-brief-rights]');
      this.rightsErrorEl = this.querySelector('[data-brief-rightserror]');
    }

    bindEvents() {
      this.addEventListener('click', this.onClick.bind(this));
      this.debouncedPersist = debounce(() => this.persistDraft(), 400);

      this.clubEl.addEventListener('input', () => {
        this.state.club = this.clubEl.value;
        if (!this.step1ErrorEl.hidden) this.step1ErrorEl.hidden = true;
        this.debouncedPersist();
      });
      this.contactNameEl.addEventListener('input', () => {
        this.state.contactName = this.contactNameEl.value;
        this.debouncedPersist();
      });
      this.emailEl.addEventListener('input', () => {
        this.state.email = this.emailEl.value;
        if (!this.emailErrorEl.hidden) this.emailErrorEl.hidden = true;
        if (!this.step1ErrorEl.hidden) this.step1ErrorEl.hidden = true;
        this.debouncedPersist();
      });
      this.activityEl.addEventListener('change', () => {
        this.state.activity = this.activityEl.value;
        this.debouncedPersist();
      });
      this.quantityEl.addEventListener('change', () => {
        this.state.quantity = this.quantityEl.value;
        this.debouncedPersist();
      });
      this.eventDateEl.addEventListener('change', () => {
        this.state.eventDate = this.eventDateEl.value;
        this.debouncedPersist();
      });
      this.designEl.addEventListener('input', () => {
        this.state.design = this.designEl.value;
        this.debouncedPersist();
      });
      this.deliveryEl.addEventListener('change', () => {
        this.state.delivery = this.deliveryEl.value;
        this.debouncedPersist();
      });
      this.rightsEl.addEventListener('change', () => {
        this.state.rightsConfirmed = this.rightsEl.checked;
        if (this.rightsEl.checked) this.rightsErrorEl.hidden = true;
        this.debouncedPersist();
      });
    }

    onClick(e) {
      let t;
      if ((t = e.target.closest('[data-brief-next]'))) {
        const target = Number(t.dataset.briefNext);
        if (target === 2 && !this.validateStep1()) return;
        this.goToStep(target);
        return;
      }
      if ((t = e.target.closest('[data-brief-back]'))) {
        this.goToStep(Number(t.dataset.briefBack));
        return;
      }
      if ((t = e.target.closest('[data-brief-chip]'))) {
        this.toggleGarment(t.dataset.key);
        return;
      }
      if ((t = e.target.closest('[data-brief-choosefiles]'))) {
        if (this.fileInputEl) this.fileInputEl.click();
        return;
      }
      if ((t = e.target.closest('[data-brief-fileremove]'))) {
        this.removeFile(t.dataset.id);
        return;
      }
      if ((t = e.target.closest('[data-brief-submit]'))) {
        this.attemptSubmit();
      }
    }

    // ---------------------------------------------------------------
    // Step navigation
    // ---------------------------------------------------------------

    validateStep1() {
      const club = this.clubEl.value.trim();
      const email = this.emailEl.value.trim();
      const emailOk = EMAIL_RE.test(email);
      const ok = club !== '' && emailOk;
      this.step1ErrorEl.hidden = ok;
      this.emailErrorEl.hidden = emailOk;
      if (!ok) {
        if (club === '') this.clubEl.focus(); else this.emailEl.focus();
      }
      return ok;
    }

    validateStep3() {
      const ok = this.rightsEl.checked;
      this.rightsErrorEl.hidden = ok;
      if (!ok) this.rightsEl.focus();
      return ok;
    }

    goToStep(n, skipPersist) {
      this.state.step = n;
      this.stepEls.forEach((el) => { el.hidden = Number(el.dataset.briefStep) !== n; });
      if (this.stepnoteEl) this.stepnoteEl.textContent = `Step ${n} of 3 · ${STEP_TITLES[n - 1]}`;
      this.syncDots();
      if (!skipPersist) this.persistDraft();
    }

    syncDots() {
      this.dotsEls.forEach((dot) => {
        const n = Number(dot.dataset.briefDot);
        const active = !this.state.submitted && this.state.step === n;
        const done = this.state.submitted || this.state.step > n;
        dot.classList.toggle('is-active', active);
        dot.classList.toggle('is-done', done);
        const numEl = dot.querySelector('[data-brief-dot-num]');
        if (numEl) numEl.textContent = done ? '✓' : String(n);
      });
    }

    // ---------------------------------------------------------------
    // Garment chips (synced with <vrt-kit-range> via the shared store)
    // ---------------------------------------------------------------

    renderChips() {
      if (!this.chipsEl) return;
      if (!this.config.garments.length) {
        this.chipsEl.innerHTML = '<span class="vrt-brief__chipsempty">Pick garments from the range above, or describe them in your design direction.</span>';
        return;
      }
      this.chipsEl.innerHTML = this.config.garments.map((g) => {
        const isSel = this.selection.has(g.key);
        return `<button type="button" class="vrt-brief__chip${isSel ? ' is-active' : ''}" data-brief-chip data-key="${esc(g.key)}">${isSel ? '✓ ' : ''}${esc(g.label)}</button>`;
      }).join('');
    }

    toggleGarment(key) {
      if (!key) return;
      if (this.selection.has(key)) this.selection.delete(key);
      else this.selection.add(key);
      this.renderChips();
      writeSelection(Array.from(this.selection), 'brief');
    }

    // ---------------------------------------------------------------
    // Upload — copy-adapted from vrt-kit-builder.js's native upload adapter
    // (that file is untouched; logic here is verbatim, renamed only). A
    // merchant-configured $0 placeholder variant accepts each file as a
    // line-item property on /cart/add.js (cart-level attributes reject file
    // uploads outright), the returned CDN URL is captured, then the line is
    // immediately zeroed via /cart/change.js so it never reaches the
    // customer's real cart/checkout. 20 MB cap (proven in kit-builder; the
    // design's "25 MB" copy is not used).
    // ---------------------------------------------------------------

    renderUploadSlot() {
      const cfg = this.config.upload;
      if (cfg && cfg.mode === 'shopify-line-item' && cfg.variantId) {
        this.uploadSlotEl.innerHTML = `
          <div class="vrt-brief__dropzone" data-brief-dropzone>
            <span class="vrt-brief__dropzone-label">↑ Drop or upload files</span>
            <span class="vrt-brief__dropzone-btn" data-brief-choosefiles>Choose files</span>
            <span class="vrt-brief__dropzone-hint">PNG · JPG · PDF · SVG · AI · EPS — max 20 MB</span>
          </div>
          <div class="vrt-brief__filelist" data-brief-filelist></div>
        `;
        this.fileInputEl = document.createElement('input');
        this.fileInputEl.type = 'file';
        this.fileInputEl.multiple = true;
        this.fileInputEl.hidden = true;
        this.fileInputEl.className = 'vrt-brief__fileinput';
        this.fileInputEl.addEventListener('change', () => {
          Array.from(this.fileInputEl.files).forEach((f) => this.uploadFile(f));
          this.fileInputEl.value = '';
        });
        this.uploadSlotEl.appendChild(this.fileInputEl);

        const dropzone = this.uploadSlotEl.querySelector('[data-brief-dropzone]');
        dropzone.addEventListener('dragover', (e) => { e.preventDefault(); dropzone.classList.add('is-dragover'); });
        dropzone.addEventListener('dragleave', () => dropzone.classList.remove('is-dragover'));
        dropzone.addEventListener('drop', (e) => {
          e.preventDefault();
          dropzone.classList.remove('is-dragover');
          Array.from(e.dataTransfer.files).forEach((f) => this.uploadFile(f));
        });

        this.filelistEl = this.uploadSlotEl.querySelector('[data-brief-filelist]');
      } else {
        this.fileInputEl = null;
        this.filelistEl = null;
        this.uploadSlotEl.innerHTML = `
          <div class="vrt-brief__uploadnote">Attach logos, artwork or references by replying to your confirmation email once you submit your brief — our team will match them up before starting the concept.</div>
        `;
      }
      this.renderFiles();
    }

    renderFiles() {
      if (!this.filelistEl) return;
      this.filelistEl.innerHTML = !this.state.files.length ? '' : this.state.files.map((f) => {
        const status = f.status === 'uploading' ? ' · uploading…' : (f.status === 'error' ? ' · couldn’t upload' : '');
        return `
          <div class="vrt-brief__fileitem${f.status === 'error' ? ' is-error' : ''}">
            <span class="vrt-brief__filename">▪ ${esc(f.name)}${status}</span>
            <button type="button" class="vrt-brief__fileremove" data-brief-fileremove data-id="${esc(f.id)}">×</button>
          </div>
        `;
      }).join('');
    }

    removeFile(id) {
      const entry = this.state.files.find((f) => f.id === id);
      if (entry && entry.abort) entry.abort.abort();
      this.state.files = this.state.files.filter((f) => f.id !== id);
      this.renderFiles();
      this.persistDraft();
    }

    uploadFile(file) {
      if (file.size > MAX_UPLOAD_BYTES) return;
      const id = 'brf' + (uploadSeq += 1);
      const abort = new AbortController();
      this.state.files.push({ id, name: file.name, status: 'uploading', url: null, abort });
      this.renderFiles();

      const cfg = this.config.upload;
      const formData = new FormData();
      formData.append('id', cfg.variantId);
      formData.append('quantity', 1);
      formData.append('properties[brief-upload]', file, file.name);

      fetch(cfg.addUrl || '/cart/add.js', { method: 'POST', body: formData, signal: abort.signal })
        .then((res) => res.ok ? res.json() : Promise.reject(new Error('add failed')))
        .then((data) => {
          const url = data.properties && data.properties['brief-upload'];
          const entry = this.state.files.find((f) => f.id === id);
          if (!entry) return; // removed while in flight
          if (url) {
            entry.status = 'done';
            entry.url = url;
          } else {
            entry.status = 'error';
          }
          this.renderFiles();
          this.persistDraft();
          // Best-effort cleanup — see vrt-kit-builder.js's identical comment:
          // a stray $0 line if this fails is an accepted risk, the URL is
          // already captured either way.
          if (data.key) {
            fetch(cfg.changeUrl || '/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: data.key, quantity: 0 }),
            }).catch(() => {});
          }
        })
        .catch(() => {
          const entry = this.state.files.find((f) => f.id === id);
          if (entry) { entry.status = 'error'; this.renderFiles(); }
        });
    }

    // ---------------------------------------------------------------
    // Submit
    // ---------------------------------------------------------------

    attemptSubmit() {
      if (this.state.step !== 3) return;
      if (!this.validateStep1()) { this.goToStep(1); return; }
      if (!this.validateStep3()) return;

      this.state.rightsConfirmed = true;
      this.state.ref = 'VRT-' + String(Math.floor(1000 + Math.random() * 9000));
      this.submitBrief();
      this.renderDone();
      this.showDone();
      clearDraft();
    }

    submitBrief() {
      const garmentLabels = Array.from(this.selection)
        .map((k) => this.garmentsByKey[k] && this.garmentsByKey[k].label)
        .filter(Boolean);
      const uploaded = this.state.files.filter((f) => f.status === 'done');
      const artwork = uploaded.length
        ? uploaded.map((f) => `${f.name} — ${f.url}`).join('\n')
        : (this.state.files.length ? 'Upload attempted but did not complete — see reply.' : 'None uploaded — may attach via reply email.');

      const payload = {
        email: this.state.email,
        ref: this.state.ref,
        club: this.state.club,
        contactName: this.state.contactName,
        activity: this.state.activity,
        garments: garmentLabels.length ? garmentLabels.join(', ') : '(none picked)',
        quantity: this.state.quantity,
        eventDate: this.state.eventDate,
        delivery: this.state.delivery,
        artwork,
        design: this.state.design,
      };

      if (this.hiddenForm) {
        this.fillContactForm(this.hiddenForm, payload);
        this.sendContactFormWithCaptcha();
      }
    }

    fillContactForm(form, payload) {
      const setField = (name, value) => {
        const input = form.querySelector(`[name="contact[${name}]"]`);
        if (input) input.value = value;
      };
      setField('Topic', 'Custom kit project brief');
      setField('email', payload.email);
      setField('Ref', payload.ref);
      setField('Club', payload.club);
      setField('Contact name', payload.contactName);
      setField('Activity', payload.activity);
      setField('Garments', payload.garments);
      setField('Quantity', payload.quantity);
      setField('Event date', payload.eventDate);
      setField('Delivery', payload.delivery);
      setField('Artwork', payload.artwork);
      setField('Artwork rights', 'Confirmed');
      setField('body', payload.design || '(no design direction provided)');
    }

    // Copy-adapted verbatim from vrt-kit-builder.js's sendContactFormWithCaptcha()
    // (~lines 986-1035) — do not simplify the polling/timeouts, this is
    // proven scar tissue (v0.9.1). Shopify's contact-form spam protection
    // (hCaptcha) needs its token solved before the POST or the backend
    // hard-rejects with "Missing CAPTCHA token". Our hidden form never gets
    // real user focusin/change events (we set .value directly), so it's
    // never auto-bound the way a normal contact form is.
    // Shopify.captcha.protect() binds+renders the (invisible) widget, but
    // its callback fires on BIND, not on SOLVE — the response field is
    // still empty right after it fires. Invisible hCaptcha requires an
    // explicit hcaptcha.execute(widgetId, {async:true}) call to actually
    // run the check and populate the token.
    sendContactFormWithCaptcha() {
      let sent = false;
      const send = () => {
        if (sent) return;
        sent = true;
        const formData = new FormData(this.hiddenForm);
        fetch(this.hiddenForm.action, { method: 'POST', body: formData })
          .then((res) => {
            // A non-OK response (network-level success, storefront-level
            // failure) still needs the guaranteed fallback: a real
            // full-navigation submit.
            if (!res.ok) this.hiddenForm.submit();
          })
          .catch(() => {
            this.hiddenForm.submit();
          });
      };

      const executeAndSend = () => {
        const start = Date.now();
        const tryExecute = () => {
          const widgetEl = this.hiddenForm.querySelector('[data-hcaptcha-widget-id]');
          const widgetId = widgetEl && widgetEl.dataset.hcaptchaWidgetId;
          if (window.hcaptcha && widgetId) {
            Promise.resolve(window.hcaptcha.execute(widgetId, { async: true })).then(send).catch(send);
          } else if (Date.now() - start < 8000) {
            // The widget id can take several seconds to attach to the DOM
            // after protect()'s bind-callback fires (~5-6s observed) — poll
            // for it rather than assume it's immediate.
            setTimeout(tryExecute, 150);
          } else {
            send();
          }
        };
        tryExecute();
      };

      const captcha = window.Shopify && window.Shopify.captcha;
      if (captcha && typeof captcha.protect === 'function') {
        captcha.protect(this.hiddenForm, executeAndSend);
        // Fail-open if the challenge never resolves (blocked script, a rare
        // visible-challenge fallback taking a while, slow network) — still
        // attempt delivery rather than hang forever; the !res.ok →
        // form.submit() fallback above still covers a missing token. Set
        // comfortably above the ~6-8s observed widget-bind + solve time.
        setTimeout(send, 20000);
      } else {
        send();
      }
    }

    // ---------------------------------------------------------------
    // Done panel (built once — the wizard body it replaces holds no live-
    // focused inputs at that point, so a fresh innerHTML here carries none
    // of the single-render risk the shell avoids elsewhere)
    // ---------------------------------------------------------------

    renderDone() {
      this.doneEl.innerHTML = `
        <div class="vrt-brief__donecard">
          <div class="vrt-brief__donemark">✓</div>
          <h3 class="vrt-brief__donetitle">Brief received.</h3>
          <p class="vrt-brief__donebody">Reference <span class="vrt-brief__doneref">#${esc(this.state.ref || '—')}</span>. We reply within one business day with a recommendation, pricing assumptions and the concept timeline. Your 24-hour concept clock starts when the brief is complete, and we'll tell you if anything is missing.</p>
        </div>
      `;
    }

    showDone() {
      this.state.submitted = true;
      if (this.wizardBodyEl) this.wizardBodyEl.hidden = true;
      this.doneEl.hidden = false;
      this.syncDots();
    }
  }

  if (!customElements.get('vrt-brief')) {
    customElements.define('vrt-brief', VrtBrief);
  }
})();
